import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Switch, Button, Typography, message, Divider, Space } from 'antd';
import { MailOutlined, SendOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Paragraph, Text } = Typography;

function EmailConfigPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [passConfigured, setPassConfigured] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { config } = await api.get('/admin/email-config');
      form.setFieldsValue({
        host: config.host,
        port: config.port,
        secure: config.secure,
        user: config.user,
        from: config.from,
        pass: '',
      });
      setPassConfigured(!!config.passConfigured);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await api.post('/admin/email-config', values);
      message.success('SMTP 配置已保存');
      setPassConfigured(true);
    } catch (e) {
      message.error('保存失败：' + (e.error || e.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testTo) return message.warning('请填写接收测试邮件的邮箱');
    setTesting(true);
    try {
      await api.post('/admin/email-config/test', { to: testTo });
      message.success(`测试邮件已发送至 ${testTo}`);
    } catch (e) {
      message.error('发送失败：' + (e.error || e.message || '未知错误'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <Title level={4}>邮件 SMTP 配置</Title>
      <Paragraph type="secondary">
        用于注册邮箱验证、系统通知等邮件发送。当前以 QQ 邮箱为例，<Text strong>SMTP 密码为邮箱「授权码」而非登录密码</Text>。
      </Paragraph>
      <Card style={{ maxWidth: 640 }}>
        <Form form={form} layout="vertical" initialValues={{ host: 'smtp.qq.com', port: 465, secure: true }}>
          <Form.Item label="SMTP 主机" name="host" rules={[{ required: true, message: '必填' }]}>
            <Input placeholder="smtp.qq.com" />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item label="端口" name="port" rules={[{ required: true }]}>
              <InputNumber min={1} max={65535} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="SSL/TLS 加密" name="secure" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item label="发件人账号（邮箱）" name="user" rules={[{ required: true, message: '必填' }]}>
            <Input placeholder="如 123456@qq.com" />
          </Form.Item>
          <Form.Item label="发件人显示名称" name="from">
            <Input placeholder="默认与账号相同" />
          </Form.Item>
          <Form.Item
            label="SMTP 密码 / 授权码"
            name="pass"
            extra={passConfigured ? '已配置，留空则保留原密码' : 'QQ 邮箱请在「设置-账户」中开启 IMAP/SMTP 并生成授权码'}
          >
            <Input.Password placeholder={passConfigured ? '留空表示不修改' : '请输入授权码'} />
          </Form.Item>
          <Space>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>保存配置</Button>
          </Space>
        </Form>

        <Divider />

        <Title level={5}>发送测试邮件</Title>
        <Space>
          <Input
            placeholder="接收测试邮件的邮箱"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            style={{ width: 280 }}
            prefix={<MailOutlined />}
          />
          <Button icon={<SendOutlined />} loading={testing} onClick={handleTest}>发送测试</Button>
        </Space>
      </Card>
    </div>
  );
}

export default EmailConfigPage;
